import React, { useEffect, useState } from 'react'
import { Spinner } from '@fluentui/react'
import { ChangeOrder, errorToString } from '@oneethos/shared'
import api from '../api-client'
import { ChangeOrderSummary } from './change-order-summary'
import { toast } from 'react-toastify'

type ChangeOrdersProps = {
  projectId: string
}

export const ChangeOrders = ({ projectId }: ChangeOrdersProps) => {
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([])
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchChangeOrders = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/loanapps/${projectId}/change-orders`)
      setChangeOrders(response || [])
    } catch (err: any) {
      setError(err.error || 'Failed to fetch change orders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (projectId) {
      fetchChangeOrders()
    }
  }, [projectId])

  const handleResend = async (coid: string) => {
    setResendingId(coid)
    api.post(`/loanapps/${projectId}/change-orders/${coid}/resend`)
      .then(() => {
        toast.success('Change order resent successfully')
        fetchChangeOrders()
      })
      .catch(err => toast.error(errorToString(err)))
      .finally(() => setResendingId(null))
  }

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <div>Error loading change orders: {error}</div>
      </div>
    )
  }

  if (changeOrders.length === 0) {
    return (
      <div className="alert alert-info">
        <div>No change orders found for this project.</div>
      </div>
    )
  }

  return (
    <div>
      <span className="fw-bold">Change Orders Requested</span>
      <p className="text-muted small mt-1">Customer must approve change order within 7 days of receiving the email.</p>
      <div className="list-group">
        {changeOrders.map((changeOrder, index) => (
          <ChangeOrderSummary
            key={changeOrder._id.toString()}
            changeOrder={changeOrder}
            isLatest={index === 0}
            resending={resendingId === changeOrder._id.toString()}
            onResend={handleResend}
          />
        ))}
      </div>
    </div>
  )
}

export default ChangeOrders
